import { useEffect, useState } from 'react';
import { Box, Breadcrumbs, Container, Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TablePagination, Typography } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ApplicationListsModal from './modals/applicationListsModal';
import DeleteApplicationListsModal from './modals/deleteApplicationListsModal';
import ClassificationBannerHeader from '../../common/banner/classifactionBannerHeader';
import PageHeader from '../../common/banner/pageHeader';
import { sortAdminItems } from '../../common/utils/tableUtil';
import SearchBar from '../../common/search/SearchBar';
import { useNavigate } from 'react-router-dom';

const ReqiuredListTable = ({ items, sortConfig, onSortChange, setRequiredList, fetchRequiredList }) => {

  const handleUpdate = (list) =>  {    
    toast.success(`${list.name} Updated`, { autoClose: 3000 });
    fetchRequiredList();
  }

  const handleDelete = (list) =>  {
    setRequiredList(items.filter(items => items.id !== list.id));
    toast.success(`${list.name} Deleted`, { autoClose: 3000 });    
  }

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  return (
      <>
        <TableContainer component={Paper} sx={{ mt: 4 }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">    
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0568AE' }}>
                {['Name', 'Action'].map((field) => (
                  <TableCell
                    key={field}
                    onClick={() => handleSort(field)}
                    sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                      <SortIcon field={field} />
                    </Stack>
                  </TableCell>                
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                  <TableCell sx={{ color: '#FFFFFF' }}>{item.name}</TableCell>
                  <TableCell>
                      <Stack direction="row" spacing={1}>
                            <ApplicationListsModal list={item} type={item.type} onTriggerUpdate={handleUpdate} />
                            <DeleteApplicationListsModal item={ item} onTriggerDelete={handleDelete} />
                      </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    )
}

export default function RequiredList() {
    const [requiredList, setRequiredList] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        fetchRequiredList();
    }, []);

    const fetchRequiredList = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/appLists/`);                    
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                const required = json.data.filter(value => value.type === 'Required');
                setRequiredList(required);
            }
        } catch (error) {
            toast.error(error);
        } 
    }

    const handleCreate = (list) => {
        toast.success(` ${list.name} Created`);
        fetchRequiredList();
    }

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    }

    const filtered = requiredList.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['name'].some((field) => item[field]?.toLowerCase().includes(s));
    })

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const title = "Application Required List";

  const handleBreadcrumbClick = () => {
    navigate('/options');
  }

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title={ title } />
        </Box>
        <Box>
            <Box sx={{ display: 'flex', mt: 2, justifyContent: 'center', gap: '32px', width: '100%', maxwidth: '1600px', margin: '30px auto 0' }}>
                <Stack  sx={{ width: '47%', paddingLeft: 0, marginLeft: 0}} spacing={{ md: 4 }}  >
                        <Breadcrumbs aria-label="breadcrumb">
                          <Link underline="hover" color="#c1d4e7" onClick={handleBreadcrumbClick} sx={{ '&:hover': { cursor: 'pointer' } }}>
                              Options
                          </Link>
                          <Typography color="white"> / </Typography>
                          <Typography color="white">{ title }</Typography>
                      </Breadcrumbs>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" sx={{ width: '50%', mx: 'auto' }}>          
                    <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Required List..."
                      label="Filter"
                      width={200}
                    />
                  </Stack>   
            </Box>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <ReqiuredListTable
                    items={paginated}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    setRequiredList={setRequiredList}
                    fetchRequiredList={fetchRequiredList}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <ApplicationListsModal list='' type='Required' onTriggerCreate={handleCreate} />
        </Box>
    </Container>
  )
}